import React, { useState, useEffect } from "react";
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Paper, 
  Grid,
  IconButton,
  LinearProgress,
  Alert,
  Divider,
  Chip
} from "@mui/material";
import { ArrowBack, CloudUpload, Delete, Image as ImageIcon, LibraryBooks } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage, db, useAuth } from "../../../firebase";
import { doc, updateDoc, arrayUnion, getDoc, setDoc } from "firebase/firestore";

// Simple background setup
const BG_IMAGE = "https://media.rainpos.com/663/hex5f021f_burgundy_design_swirl_boldtiny09867633333333.jpg";

interface UploadedFile {
  name: string;
  url: string;
  size: number;
  uploadedAt?: string;
}

const MediaManagerStudio: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  
  const [orderFiles, setOrderFiles] = useState<UploadedFile[]>([]);
  const [globalFiles, setGlobalFiles] = useState<UploadedFile[]>([]);
  
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");

  // Fetch Files
  useEffect(() => {
    const fetchFiles = async () => {
      if (!user) return;

      try {
        // 1. Fetch Global Library
        const userDocRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
           const data = userSnap.data();
           if (data.mediaLibrary) {
             setGlobalFiles(data.mediaLibrary);
           }
        }

        // 2. Fetch Order Files (if applicable)
        if (orderId) {
           const orderDocRef = doc(db, "orders", orderId);
           const orderSnap = await getDoc(orderDocRef);
           if (orderSnap.exists()) {
              const data = orderSnap.data();
              if (data.uploadedFiles) {
                setOrderFiles(data.uploadedFiles);
              }
           }
        }
      } catch (err) {
        console.error("Error fetching files", err);
      }
    };
    
    fetchFiles();
  }, [user, orderId]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !user) return;
    const fileList = Array.from(event.target.files);
    
    setIsUploading(true);
    setError("");

    // Process one by one
    for (const file of fileList) {
       try {
          // Determine storage path
          // If we allow "Global" uploads, we ideally store in a user folder.
          // For legacy/order compat: if orderId exists, we can still put it in 'orders' folder, 
          // OR we unify everything to 'users/{uid}/uploads'.
          // Let's use 'users/{uid}/uploads' for standardized ownership, 
          // but if orderId is strictly present, we can stick to order folder?
          // Decision: Store in User Folder for easier reuse logic.
          const storagePath = `users/${user.uid}/uploads/${Date.now()}_${file.name}`;
          const storageRef = ref(storage, storagePath);
          const uploadTask = uploadBytesResumable(storageRef, file);

          await new Promise<void>((resolve, reject) => {
              uploadTask.on('state_changed', 
                (snapshot) => {
                   const prog = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                   setUploadProgress(prog);
                },
                (error) => reject(error),
                async () => {
                   const url = await getDownloadURL(uploadTask.snapshot.ref);
                   const fileData: UploadedFile = { 
                     name: file.name, 
                     url, 
                     size: file.size, 
                     uploadedAt: new Date().toISOString() 
                   };
                   
                   // 1. Add to Global Library (User Doc)
                   // Check if user doc exists first
                   const userDocRef = doc(db, "users", user.uid);
                   // We use setDoc with merge to ensure doc exists
                   await setDoc(userDocRef, {
                      mediaLibrary: arrayUnion(fileData)
                   }, { merge: true });
                   
                   setGlobalFiles(prev => [...prev, fileData]);

                   // 2. If Order Context, Add to Order
                   if (orderId) {
                     await updateDoc(doc(db, "orders", orderId), {
                        uploadedFiles: arrayUnion(fileData)
                     });
                     setOrderFiles(prev => [...prev, fileData]);
                   }
                   
                   resolve();
                }
              );
          });
       } catch (err) {
          console.error("Upload failed", err);
          setError("One or more files failed to upload.");
       }
    }
    setIsUploading(false);
    setUploadProgress(0);
  };

  const FileGrid = ({ files, title }: { files: UploadedFile[], title: string }) => (
     <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{title} ({files.length})</Typography>
        {files.length === 0 ? (
           <Typography color="text.secondary" fontStyle="italic">No files.</Typography>
        ) : (
           <Grid container spacing={2}>
              {files.map((file, i) => (
                 <Grid size={{ xs: 6, sm: 4, md: 3 }} key={i}>
                    <Paper variant="outlined" sx={{ p: 1, position: 'relative' }}>
                       <Box sx={{ height: 150, bgcolor: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, overflow: 'hidden' }}>
                          {file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i) || file.url.includes('firebasestorage') ? (
                            <img src={file.url} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <ImageIcon fontSize="large" color="disabled" />
                          )}
                       </Box>
                       <Typography noWrap variant="caption" display="block">{file.name}</Typography>
                    </Paper>
                 </Grid>
              ))}
           </Grid>
        )}
     </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "#f5f5f5" }}>
      {/* Header */}
      <Box sx={{ 
        height: "100px", 
        backgroundImage: `url('${BG_IMAGE}')`, 
        backgroundSize: "cover",
        display: "flex", alignItems: "center"
      }}>
        <Container maxWidth="lg">
            <Button 
              startIcon={<ArrowBack />} 
              sx={{ color: "white" }}
              onClick={() => navigate(orderId ? `/account/orders/${orderId}` : '/account')}
            >
              {orderId ? "Back to Order" : "Back to Studio"}
            </Button>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ mt: -4, mb: 8, flexGrow: 1 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h4" sx={{ fontFamily: '"Playfair Display", serif' }}>
                Media Manager
              </Typography>
              {orderId && <Chip label={`Order #${orderId.slice(0,8)}`} color="primary" variant="outlined" />}
            </Box>
            
            <Typography variant="body1" color="text.secondary" paragraph>
              Upload distinct, high-quality photos for your artwork. Files are saved to your personal library for easy reuse.
            </Typography>

            {/* Upload Area */}
            <Box 
              sx={{ 
                border: '2px dashed #ccc', 
                borderRadius: 2, 
                p: 6, 
                textAlign: 'center',
                bgcolor: '#fafafa',
                cursor: 'pointer',
                '&:hover': { bgcolor: '#f0f0f0', borderColor: '#999' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transition: 'all 0.2s'
              }}
              component="label"
            >
               <input 
                 type="file" 
                 hidden 
                 multiple 
                 accept="image/*,.zip" 
                 onChange={handleFileUpload}
                 disabled={isUploading}
               />
               <CloudUpload sx={{ fontSize: 60, color: '#aaa', mb: 2 }} />
               <Typography variant="h6" color="text.secondary">
                 Click to Select Photos
               </Typography>
               <Typography variant="caption" color="text.secondary">
                 JPG, PNG, or ZIP files accepted.
               </Typography>
            </Box>

            {/* Progress Bar */}
            {isUploading && (
              <Box sx={{ mt: 4 }}>
                 <Typography variant="caption">Uploading...</Typography>
                 <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            <Divider sx={{ my: 4 }} />

            {/* Two Sections if Order is Active */}
            
            {orderId && (
              <>
                <FileGrid files={orderFiles} title="Attached to This Order" />
                <Divider sx={{ my: 4 }}><Chip label="Your Global Library" /></Divider>
              </>
            )}
            
            <FileGrid files={globalFiles} title={orderId ? "All Your Uploads" : "Your Library"} />
            
        </Paper>
      </Container>
    </Box>
  );
};

export default MediaManagerStudio;
